import { useContext } from "react";

import FormContext from "./FormContext";

/**
 * FormFieldValue Component
 *
 * A component that provides access to a specific form field's value and a setter function
 * to update that value. This component uses the FormContext to manage form state.
 *
 * @param name - The name/identifier of the form field
 * @param render - A render function that receives the field value and setter function
 * @returns The rendered component based on the render prop pattern
 *
 * @example
 * // Basic usage:
 * <FormFieldValue
 *   name="username"
 *   render={(value, setValue) => (
 *     <input
 *       type="text"
 *       value={value || ''}
 *       onChange={(e) => setValue(e.target.value)}
 *     />
 *   )}
 * />
 *
 * @example
 * // Usage in training edit form:
 * <FormFieldValue
 *   name="trainingData"
 *   render={(value, setValue) => (
 *     <TrainingEditor
 *       data={value || []}
 *       onChange={(newData) => setValue(newData)}
 *     />
 *   )}
 * />
 */
const FormFieldValue = ({
  name,
  render,
}: {
  name: string;
  render: (value: any, setValue: (value: any) => void) => React.ReactNode;
}) => {
  const context = useContext(FormContext);
  const field = context.get(name) || { value: undefined };

  const setValue = (value: any) => {
    context.set(name, value);
  };

  return <>{render(field.value, setValue)}</>;
};

export default FormFieldValue;
